// The world, as tiles.
//
// There are two places: the field and the one room you can walk into. They are
// stamped into the *same* array and are kept apart by `zoneAt` rather than by
// distance — no walkable tile joins them, the doors are teleports, and every
// query that could let one place see the other (targets, chases, snapshots,
// the camera) goes through the zone predicate. That is cheaper than two grids
// and it means "which place is this body in" has exactly one answer.
//
// The map is generated from a fixed seed, so the server and the client build
// the identical grid without one of them having to send it.

import {
  FIELD_H,
  FIELD_W,
  GRID_HALF,
  SHOP_H,
  SHOP_TOP,
  SHOP_W,
  TILE,
  WORLD_TILES_H,
  WORLD_TILES_W,
} from "./constants";
import { makeRng } from "./rng";
import type { Zone } from "./types";

export const TILE_ROCK = 0;
export const TILE_GRASS = 1;
export const TILE_PATH = 2;
export const TILE_TREE = 3;
export const TILE_WALL = 4;
export const TILE_FLOOR = 5;
/** The doorway in the field wall. Stepping on it puts you in the shop. */
export const TILE_DOOR_IN = 6;
/** The doorway in the shop wall. Stepping on it puts you back in the field. */
export const TILE_DOOR_OUT = 7;

export type Tile = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

const SOLID: ReadonlySet<Tile> = new Set<Tile>([TILE_ROCK, TILE_TREE, TILE_WALL]);

export interface Point {
  x: number;
  y: number;
}

export interface WorldMap {
  tiles: Tile[];
  /** Where a new or respawning body starts. */
  spawn: Point;
  /** Where the shopkeeper stands. */
  keeper: Point;
  /** Where each door puts you down. */
  intoShop: Point;
  intoField: Point;
}

/** The shop building's footprint in the field, and its doorway. */
const SHOP_BUILDING = { x: 24, y: 5, w: 7, h: 5 } as const;
const DOOR_TILE = { x: 27, y: 9 } as const;
/** The shop interior's doorway, in the middle of the room's bottom wall. */
const SHOP_EXIT_TILE = { x: Math.floor(SHOP_W / 2), y: SHOP_TOP + SHOP_H - 1 } as const;

export const tileIndex = (tx: number, ty: number): number => ty * WORLD_TILES_W + tx;

const centre = (tx: number, ty: number): Point => ({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 });

export function tileAt(map: WorldMap, tx: number, ty: number): Tile {
  if (tx < 0 || ty < 0 || tx >= WORLD_TILES_W || ty >= WORLD_TILES_H) return TILE_ROCK;
  return map.tiles[tileIndex(tx, ty)] ?? TILE_ROCK;
}

export const tileAtPoint = (map: WorldMap, x: number, y: number): Tile =>
  tileAt(map, Math.floor(x / TILE), Math.floor(y / TILE));

export const isSolidTile = (tile: Tile): boolean => SOLID.has(tile);

/** Which of the two places a world coordinate is in. The one predicate that
 *  keeps the field and the room from ever touching. */
export const zoneAt = (_x: number, y: number): Zone => (y < FIELD_H * TILE ? 0 : 1);

/** The rectangle the camera is allowed to show, in world pixels. Looking out
 *  of your own zone would show the bedrock between the two of them. */
export function zoneBounds(zone: Zone): { x: number; y: number; w: number; h: number } {
  if (zone === 0) return { x: 0, y: 0, w: FIELD_W * TILE, h: FIELD_H * TILE };
  return { x: 0, y: SHOP_TOP * TILE, w: SHOP_W * TILE, h: SHOP_H * TILE };
}

/** Does a one-cell body centred here overlap anything solid? */
export function boxHitsSolid(map: WorldMap, x: number, y: number, half = GRID_HALF): boolean {
  const x0 = Math.floor((x - half) / TILE);
  const x1 = Math.floor((x + half - 0.001) / TILE);
  const y0 = Math.floor((y - half) / TILE);
  const y1 = Math.floor((y + half - 0.001) / TILE);
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      if (isSolidTile(tileAt(map, tx, ty))) return true;
    }
  }
  return false;
}

/** The door a body standing here is on, if any, and where it comes out. */
export function doorAt(map: WorldMap, x: number, y: number): Point | undefined {
  const tile = tileAtPoint(map, x, y);
  if (tile === TILE_DOOR_IN) return map.intoShop;
  if (tile === TILE_DOOR_OUT) return map.intoField;
  return undefined;
}

const inRect = (tx: number, ty: number, r: { x: number; y: number; w: number; h: number }): boolean =>
  tx >= r.x && ty >= r.y && tx < r.x + r.w && ty < r.y + r.h;

export function buildWorldMap(): WorldMap {
  const tiles: Tile[] = new Array(WORLD_TILES_W * WORLD_TILES_H).fill(TILE_ROCK);
  const rng = makeRng(0x5eed);
  const set = (tx: number, ty: number, tile: Tile): void => {
    tiles[tileIndex(tx, ty)] = tile;
  };
  const get = (tx: number, ty: number): Tile => tiles[tileIndex(tx, ty)] ?? TILE_ROCK;

  // --- the field ------------------------------------------------------------
  for (let ty = 0; ty < FIELD_H; ty++) {
    for (let tx = 0; tx < FIELD_W; tx++) {
      const edge = tx === 0 || ty === 0 || tx === FIELD_W - 1 || ty === FIELD_H - 1;
      set(tx, ty, edge ? TILE_TREE : TILE_GRASS);
    }
  }

  // A dirt path from the spawn clearing to the shop door, so the one building
  // in the world is not something you have to find.
  const spawnTile = { x: 6, y: 19 };
  for (let tx = spawnTile.x; tx <= DOOR_TILE.x; tx++) set(tx, spawnTile.y, TILE_PATH);
  for (let ty = DOOR_TILE.y + 1; ty <= spawnTile.y; ty++) set(DOOR_TILE.x, ty, TILE_PATH);

  // Scattered trees. Deliberately sparse and never on the path: the field is
  // meant to read as "an outdoors", not as a maze.
  for (let i = 0; i < 70; i++) {
    const tx = 2 + Math.floor(rng.next() * (FIELD_W - 4));
    const ty = 2 + Math.floor(rng.next() * (FIELD_H - 4));
    if (get(tx, ty) !== TILE_GRASS) continue;
    if (inRect(tx, ty, { x: SHOP_BUILDING.x - 1, y: SHOP_BUILDING.y - 1, w: SHOP_BUILDING.w + 2, h: SHOP_BUILDING.h + 3 })) continue;
    if (Math.abs(tx - spawnTile.x) < 3 && Math.abs(ty - spawnTile.y) < 3) continue;
    set(tx, ty, TILE_TREE);
  }

  // The shop building: a solid block with one doorway in its bottom wall.
  for (let ty = SHOP_BUILDING.y; ty < SHOP_BUILDING.y + SHOP_BUILDING.h; ty++) {
    for (let tx = SHOP_BUILDING.x; tx < SHOP_BUILDING.x + SHOP_BUILDING.w; tx++) set(tx, ty, TILE_WALL);
  }
  set(DOOR_TILE.x, DOOR_TILE.y, TILE_DOOR_IN);

  // --- the shop interior ----------------------------------------------------
  for (let ty = 0; ty < SHOP_H; ty++) {
    for (let tx = 0; tx < SHOP_W; tx++) {
      const edge = tx === 0 || ty === 0 || tx === SHOP_W - 1 || ty === SHOP_H - 1;
      set(tx, SHOP_TOP + ty, edge ? TILE_WALL : TILE_FLOOR);
    }
  }
  set(SHOP_EXIT_TILE.x, SHOP_EXIT_TILE.y, TILE_DOOR_OUT);

  return {
    tiles,
    spawn: centre(spawnTile.x, spawnTile.y),
    keeper: centre(SHOP_EXIT_TILE.x, SHOP_TOP + 3),
    // A door puts you down one cell clear of the doorway you arrived through,
    // so you are never standing on the thing that just moved you.
    intoShop: centre(SHOP_EXIT_TILE.x, SHOP_EXIT_TILE.y - 1),
    intoField: centre(DOOR_TILE.x, DOOR_TILE.y + 1),
  };
}
